import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import "./estilodash.css";

const ClientesList = () => {
  const [clientes, setClientes] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroIsento, setFiltroIsento] = useState("todos");
  const [filtroPlano, setFiltroPlano] = useState("todos");

  const editRef = useRef(null);

  useEffect(() => {
    carregarTudo();
  }, []);

  useEffect(() => {
    if (clienteSelecionado && editRef.current) {
      editRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [clienteSelecionado]);

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setErro(null);

      const [respClientes, respLicencas, respPlanos] = await Promise.all([
        api.get("/api/clientes"),
        api.get("/api/licencas-v2"),
        api.get("/api/planos-ativos"),
      ]);

      setClientes(respClientes.data || []);
      setLicencas(respLicencas.data || []);
      setPlanos(respPlanos.data || []);

      const filtroId = localStorage.getItem("clienteFiltroId");
      if (filtroId) {
        const cli = (respClientes.data || []).find(
          (c) => String(c.id) === filtroId
        );
        if (cli) {
          setBusca(cli.nome || filtroId);
        }
        localStorage.removeItem("clienteFiltroId");
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setErro("Erro ao carregar dados. Verifique o login/servidor.");
    } finally {
      setCarregando(false);
    }
  };

  const getPlanoById = (planoId) => {
    if (!planoId) return null;
    return planos.find((p) => Number(p.id) === Number(planoId)) || null;
  };

  const calcularExtras = (cliente) => {
    const plano = getPlanoById(cliente.plano_id);
    const slotsContratados = Number(cliente.slots_contratados ?? 0);
    const slotsIncluidos = plano ? Number(plano.slots_inclusos ?? 0) : 0;
    const extras = Math.max(slotsContratados - slotsIncluidos, 0);
    return { slotsContratados, slotsIncluidos, extras };
  };

  const getLicencasCadastradas = (clienteId) => {
    return licencas.filter(
      (lic) => Number(lic.cliente_id || lic.id_cliente) === Number(clienteId)
    ).length;
  };

  const handleAlterarStatus = async (cliente) => {
    const novoStatus = String(cliente.status) === "1" ? "0" : "1";

    if (
      !window.confirm(
        `Deseja realmente ${
          novoStatus === "1" ? "ATIVAR" : "DESATIVAR"
        } o cliente "${cliente.nome}"?`
      )
    ) {
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/status`, {
        status: novoStatus,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao alterar o status do cliente:", error);
      alert("Erro ao alterar o status do cliente.");
    }
  };

  const handleToggleIsento = async (cliente) => {
    const novoIsento = String(cliente.isento) === "1" ? "0" : "1";

    if (
      !window.confirm(
        `Deseja marcar o cliente "${cliente.nome}" como ${
          novoIsento === "1" ? "ISENTO" : "NÃO ISENTO"
        }?`
      )
    ) {
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/isento`, {
        isento: novoIsento,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao atualizar isento do cliente:", error);
      alert("Erro ao atualizar isento do cliente.");
    }
  };

  const handleAlterarSlots = async (cliente) => {
    const atual = cliente.slots_contratados ?? 2;

    const entrada = window.prompt(
      `Informe o número de SLOTS contratados para o cliente "${cliente.nome}":`,
      atual
    );

    if (entrada === null) return;

    const slots = parseInt(entrada, 10);
    if (isNaN(slots) || slots < 1) {
      alert("Valor inválido. Informe um número inteiro maior ou igual a 1.");
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/slots`, {
        slots_contratados: slots,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao atualizar slots contratados do cliente:", error);
      alert("Erro ao atualizar slots contratados.");
    }
  };

  const handleEditarCliente = (cliente) => {
    setClienteSelecionado({
      ...cliente,
      slots_contratados: cliente.slots_contratados ?? "",
      dia_vencimento: cliente.dia_vencimento ?? "",
    });
  };

  const handleCancelarEdicao = () => {
    setClienteSelecionado(null);
  };

  const handleSalvarEdicao = async () => {
    if (!clienteSelecionado) return;

    let diaVenc = clienteSelecionado.dia_vencimento;

    if (diaVenc !== undefined && diaVenc !== null && diaVenc !== "") {
      diaVenc = parseInt(diaVenc, 10);
      if (isNaN(diaVenc) || diaVenc < 1 || diaVenc > 28) {
        alert("Dia de vencimento inválido. Informe um número entre 1 e 28.");
        return;
      }
    } else {
      diaVenc = undefined;
    }

    try {
      await api.put(`/api/clientes/${clienteSelecionado.id}`, {
        nome: clienteSelecionado.nome,
        email: clienteSelecionado.email,
        phone: clienteSelecionado.phone,
        cpf: clienteSelecionado.cpf,
        slots_contratados: clienteSelecionado.slots_contratados,
        dia_vencimento: diaVenc,
      });

      setClienteSelecionado(null);
      carregarTudo();
    } catch (error) {
      console.error("Erro ao salvar o cliente:", error);
      alert("Erro ao salvar o cliente.");
    }
  };

  const handleRemoverCliente = async (id) => {
    if (!window.confirm("Deseja realmente remover este cliente?")) return;

    try {
      await api.delete(`/api/clientes/${id}`);
      carregarTudo();
    } catch (error) {
      console.error("Erro ao remover o cliente:", error);
      alert("Erro ao remover o cliente.");
    }
  };

  let clientesFiltrados = clientes.filter((cliente) => {
    if (!busca) return true;
    const termo = busca.toLowerCase();
    return (
      String(cliente.id).includes(termo) ||
      (cliente.nome && cliente.nome.toLowerCase().includes(termo)) ||
      (cliente.email && cliente.email.toLowerCase().includes(termo)) ||
      (cliente.cpf && String(cliente.cpf).toLowerCase().includes(termo)) ||
      (cliente.phone && String(cliente.phone).toLowerCase().includes(termo))
    );
  });

  if (filtroStatus === "ativos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.status) === "1"
    );
  } else if (filtroStatus === "inativos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.status) !== "1"
    );
  }

  if (filtroIsento === "isentos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.isento) === "1"
    );
  } else if (filtroIsento === "nao-isentos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.isento) !== "1"
    );
  }

  if (filtroPlano !== "todos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.plano_id || "") === String(filtroPlano)
    );
  }

  const clientesOrdenados = useMemo(() => {
    return [...clientesFiltrados].sort((a, b) => Number(b.id) - Number(a.id));
  }, [clientesFiltrados]);

  return (
    <div className="pagamentos-container">
      <h3>Clientes</h3>

      {erro && (
        <div
          style={{
            background: "#fdecec",
            color: "#7f1d1d",
            border: "1px solid #f5b7b7",
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <input
          type="text"
          placeholder="Buscar por ID, nome, email, cpf, telefone..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />

        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="todos">Todos os status</option>
          <option value="ativos">Ativos</option>
          <option value="inativos">Inativos</option>
        </select>

        <select
          value={filtroIsento}
          onChange={(e) => setFiltroIsento(e.target.value)}
        >
          <option value="todos">Todos</option>
          <option value="isentos">Isentos</option>
          <option value="nao-isentos">Não isentos</option>
        </select>

        <select
          value={filtroPlano}
          onChange={(e) => setFiltroPlano(e.target.value)}
        >
          <option value="todos">Todos os planos</option>
          {planos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nome}
            </option>
          ))}
        </select>
      </div>

      {clienteSelecionado && (
        <div
          ref={editRef}
          style={{
            marginBottom: 24,
            background: "#f8f9fb",
            border: "2px solid #7c3aed",
            borderRadius: 8,
            padding: 16,
          }}
        >
          <h4 style={{ marginTop: 0 }}>
            Editando cliente #{clienteSelecionado.id} - {clienteSelecionado.nome}
          </h4>

          <div className="form-linha">
            <label>
              Nome
              <input
                type="text"
                value={clienteSelecionado.nome || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    nome: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              E-mail
              <input
                type="email"
                value={clienteSelecionado.email || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="form-linha">
            <label>
              Telefone
              <input
                type="text"
                value={clienteSelecionado.phone || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              CPF
              <input
                type="text"
                value={clienteSelecionado.cpf || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    cpf: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="form-linha">
            <label>
              Slots contratados
              <input
                type="number"
                min="1"
                value={clienteSelecionado.slots_contratados || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    slots_contratados: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              Dia de vencimento
              <input
                type="number"
                min="1"
                max="28"
                value={clienteSelecionado.dia_vencimento || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    dia_vencimento: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div
            className="cliente-acoes"
            style={{ justifyContent: "flex-start", marginTop: 10 }}
          >
            <button
              type="button"
              className="cliente-btn cliente-btn-editar"
              onClick={handleSalvarEdicao}
            >
              Salvar
            </button>

            <button
              type="button"
              className="cliente-btn cliente-btn-remover"
              onClick={handleCancelarEdicao}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {carregando ? (
        <p>Carregando clientes...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>CPF</th>
                <th>Status</th>
                <th>Isento</th>
                <th>Plano legado</th>
                <th>Slots</th>
                <th>Extras</th>
                <th>Licenças</th>
                <th>Vencimento</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {clientesOrdenados.map((cliente) => {
                const plano = getPlanoById(cliente.plano_id);
                const { slotsContratados, extras } = calcularExtras(cliente);
                const qtdLicencas = getLicencasCadastradas(cliente.id);
                const ativo = String(cliente.status) === "1";
                const isento = String(cliente.isento) === "1";

                return (
                  <tr key={cliente.id} className={!ativo ? "linha-inativa" : ""}>
                    <td>{cliente.id}</td>
                    <td>{cliente.nome}</td>
                    <td>{cliente.email}</td>
                    <td>{cliente.phone || "—"}</td>
                    <td>{cliente.cpf || "—"}</td>
                    <td>
                      {ativo ? (
                        <span className="badge badge-ativo">Ativo</span>
                      ) : (
                        <span className="badge badge-inativo">Inativo</span>
                      )}
                    </td>
                    <td>
                      {isento ? (
                        <span className="badge badge-isento">Isento</span>
                      ) : (
                        <span className="badge badge-normal">Normal</span>
                      )}
                    </td>
                    <td>{plano?.nome || "—"}</td>
                    <td>{slotsContratados}</td>
                    <td>{extras}</td>
                    <td>{qtdLicencas}</td>
                    <td>{cliente.dia_vencimento || "—"}</td>
                    <td>
                      <div className="cliente-acoes">
                        <button
                          type="button"
                          className="cliente-btn cliente-btn-editar"
                          onClick={() => handleEditarCliente(cliente)}
                        >
                          Editar
                        </button>

                        <button
                          type="button"
                          className="cliente-btn cliente-btn-slots"
                          onClick={() => handleAlterarSlots(cliente)}
                        >
                          Slots
                        </button>

                        <button
                          type="button"
                          className="cliente-btn cliente-btn-isento"
                          onClick={() => handleToggleIsento(cliente)}
                        >
                          {isento ? "Tirar isenção" : "Isentar"}
                        </button>

                        <button
                          type="button"
                          className={`cliente-btn ${
                            ativo
                              ? "cliente-btn-desativar"
                              : "cliente-btn-ativar"
                          }`}
                          onClick={() => handleAlterarStatus(cliente)}
                        >
                          {ativo ? "Desativar" : "Ativar"}
                        </button>

                        <button
                          type="button"
                          className="cliente-btn cliente-btn-remover"
                          onClick={() => handleRemoverCliente(cliente.id)}
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientesList;